import { formatCurrency, formatNumber } from '../../utils/format';
import { normalizeSignupSourceRows } from '../../utils/signupSource';

const PAID_SLUGS = new Set(['basic', 'premium', 'enterprise']);

export const SIGNUP_TIER_MIX_LABEL = 'signup-tier mix (not ADS paid)';

export const WEEKLY_CHECKLIST_ITEM_IDS = [
  'review_kpis',
  'review_growth_spend',
  'review_cro',
  'review_activation',
  'review_fail_count',
];

export const WEEKLY_CHECKLIST_PARTIAL_CAPTION =
  'Weekly gaps checklist incomplete until later waves';

export const WEEKLY_CHECKLIST_COMPLETE_CAPTION =
  'Check off each item during the weekly GTM review.';

/** Prior AWG waves shipped in this package (W5b enables checklist rows). */
export const WAVES_SHIPPED = { cro: true, ads: true, act: true, rev: true };

const GROWTH_SPEND_CHECKLIST_ID = 'review_growth_spend';
const FAIL_COUNT_CHECKLIST_ID = 'review_fail_count';

/** @param {string} [env] REACT_APP_ENV or `local` when unset. */
export function weeklyChecklistStorageKey(env) {
  const normalized = env && String(env).trim() ? String(env).trim() : 'local';
  return `ffc.admin.weeklyChecklist.${normalized}`;
}

export function isWeeklyChecklistItemEnabled(itemId, wavesShipped = WAVES_SHIPPED, { isAdmin = false } = {}) {
  if (itemId === 'review_kpis') return true;
  if (itemId === 'review_growth_spend') return Boolean(wavesShipped?.ads);
  if (itemId === 'review_cro') return Boolean(wavesShipped?.cro);
  if (itemId === 'review_activation') return Boolean(wavesShipped?.act);
  if (itemId === FAIL_COUNT_CHECKLIST_ID) return Boolean(wavesShipped?.rev) && isAdmin;
  return false;
}

/** W5b: `review_kpis` always on; other ids follow wave flags (fail count Admin-only). */
export function weeklyChecklistItemDefinitions(
  wavesShipped = WAVES_SHIPPED,
  { isAdmin = false } = {}
) {
  const defs = [
    { id: 'review_kpis', label: 'Review Home KPIs' },
    { id: 'review_growth_spend', label: 'Review Growth spend and CPA' },
    { id: 'review_cro', label: 'Review Product signup funnel diagnosis' },
    { id: 'review_activation', label: 'Review activation milestones' },
    { id: FAIL_COUNT_CHECKLIST_ID, label: 'Review Revenue payment fails' },
  ];
  return defs.map((row) => ({
    ...row,
    enabled: isWeeklyChecklistItemEnabled(row.id, wavesShipped, { isAdmin }),
  }));
}

/** Items that apply to the current operator (fail count is Admin-only). */
export function weeklyChecklistItemsForRole(items, { isAdmin = false } = {}) {
  return (items || []).filter(
    (item) => item.id !== FAIL_COUNT_CHECKLIST_ID || isAdmin
  );
}

export function isWeeklyChecklistComplete(items, { isAdmin = false } = {}) {
  const relevant = weeklyChecklistItemsForRole(items, { isAdmin });
  return relevant.length > 0 && relevant.every((item) => item.enabled);
}

export function weeklyChecklistCaption(items, { isAdmin = false } = {}) {
  return isWeeklyChecklistComplete(items, { isAdmin })
    ? WEEKLY_CHECKLIST_COMPLETE_CAPTION
    : WEEKLY_CHECKLIST_PARTIAL_CAPTION;
}

export function isGrowthSpendChecklistEnabled(items = weeklyChecklistItemDefinitions()) {
  return items.some((item) => item.id === GROWTH_SPEND_CHECKLIST_ID && item.enabled);
}

export function parseWeeklyChecklistState(rawJson, items) {
  let stored = {};
  if (rawJson) {
    try {
      const parsed = JSON.parse(rawJson);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        stored = parsed;
      }
    } catch {
      stored = {};
    }
  }
  const checked = {};
  for (const item of items || []) {
    if (!item.enabled) continue;
    checked[item.id] = stored[item.id] === true;
  }
  return checked;
}

/** Persist only enabled item ids that are checked. */
export function serializeWeeklyChecklistChecked(checkedById, items) {
  const out = {};
  for (const item of items || []) {
    if (!item.enabled) continue;
    if (checkedById?.[item.id] === true) out[item.id] = true;
  }
  return out;
}

export function toggleWeeklyChecklistItem(checkedById, itemId, items) {
  const item = (items || []).find((row) => row.id === itemId);
  if (!item?.enabled) return checkedById || {};
  const next = { ...(checkedById || {}) };
  next[itemId] = !next[itemId];
  return next;
}

/**
 * Slack-ready plain text for the weekly GTM memo (AWG-MON-1).
 * `mix` is signup-tier counts — not Growth ADS paid signups.
 */
/** Memo line from `largestAdjacentDrop` result (Product diagnosis). */
export function formatPaidFunnelDropMemo(drop) {
  if (!drop || drop.drop <= 0) return null;
  const ratePct =
    drop.fromCount > 0 ? `${Math.round((drop.toCount / drop.fromCount) * 100)}%` : '—';
  return `Paid funnel largest drop: ${drop.fromLabel} → ${drop.toLabel} (${formatNumber(
    drop.fromCount
  )} → ${formatNumber(drop.toCount)}, −${formatNumber(drop.drop)}, ${ratePct} of previous)`;
}

export function formatPaymentFailCountMemo(count) {
  if (count == null || !Number.isFinite(Number(count))) return null;
  return `Failed payments (distinct users): ${formatNumber(count)}`;
}

export function buildHomeWeeklyMemo({
  kpi,
  mix,
  topSources,
  mrr,
  paidFunnelDrop,
  paymentFailCount,
  growthSpendChecklistEnabled = false,
}) {
  const lines = ['Weekly GTM snapshot', ''];

  const customers = kpi?.customers ?? kpi?.totalUsers;
  const signups = kpi?.signups;
  lines.push(`Customers: ${formatNumber(customers)}`);
  lines.push(`Signups: ${formatNumber(signups)}`);

  if (mix) {
    lines.push(`${SIGNUP_TIER_MIX_LABEL}: ${signupMixHint(mix)}`);
  } else {
    lines.push(`${SIGNUP_TIER_MIX_LABEL}: —`);
  }

  lines.push('');
  if (mrr?.mrrCents != null) {
    lines.push(`MRR: ${formatCurrency(mrr.mrrCents, mrr.currency)}`);
  } else {
    lines.push('MRR: —');
  }

  lines.push('');
  lines.push('Top signup sources:');
  const sources = topSources || [];
  if (sources.length === 0) {
    lines.push('(none in range)');
  } else {
    for (const row of sources) {
      lines.push(`• ${row.source}: ${formatNumber(row.count)}`);
    }
  }

  const dropLine =
    typeof paidFunnelDrop === 'string' ? paidFunnelDrop : formatPaidFunnelDropMemo(paidFunnelDrop);
  if (dropLine) {
    lines.push('');
    lines.push(dropLine);
  }

  const failLine =
    typeof paymentFailCount === 'string'
      ? paymentFailCount
      : formatPaymentFailCountMemo(paymentFailCount);
  if (failLine) {
    lines.push('');
    lines.push(failLine);
  }

  if (!growthSpendChecklistEnabled) {
    lines.push('');
    lines.push('Open Growth for spend.');
  }

  return lines.join('\n');
}

/** Paid vs free vs everything else (unknown / other) for the Home signup line. */
export function splitSignupTiers(rows) {
  let paid = 0;
  let free = 0;
  let other = 0;
  for (const row of rows || []) {
    const count = Number(row?.count);
    const n = Number.isFinite(count) ? count : 0;
    const slug = row?.slug || 'unknown';
    if (slug === 'free') free += n;
    else if (PAID_SLUGS.has(slug)) paid += n;
    else other += n;
  }
  return { paid, free, other };
}

export function signupMixHint({ paid, free, other }) {
  const parts = [`${paid} paid`, `${free} free`];
  if (other > 0) parts.push(`${other} unknown`);
  return parts.join(' · ');
}

/** Highest counts first. Ties keep API order. */
export function topSignupSources(rows, limit = 3) {
  return normalizeSignupSourceRows(rows)
    .sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0))
    .slice(0, limit);
}

export function isViolationSpike(current, prior) {
  if (current == null || prior == null) return false;
  if (current <= prior) return false;
  if (prior === 0) return current >= 5;
  return current >= prior * 2;
}

/** Alerts only. Fresh rollup and a quiet range produce an empty list. */
export function homeAttention({ freshness, signups, violations, priorViolations }) {
  const alerts = [];
  if (freshness?.rollupStatus === 'stale') {
    alerts.push('Rollup is stale. Refresh it before trusting these numbers.');
  }
  if (freshness?.stripeCacheStatus === 'stale') {
    alerts.push('Stripe cache is stale. MRR may be behind.');
  }
  if (signups === 0) {
    alerts.push('No signups in this range.');
  }
  if (isViolationSpike(violations, priorViolations)) {
    alerts.push(`Limit violations jumped to ${violations} from ${priorViolations}.`);
  }
  return alerts;
}
